import { X, User } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { isCollectionPath } from '@/features/contributor-workspace/workspace-destinations';
import { useActiveCollectorSession } from '../useActiveCollectorSession';

/**
 * Global sticky bar displayed below the navbar on ALL pages when a collector session is active.
 * Shows the active community profile code and allows disconnection.
 * Contributions made while this bar is visible are attributed to the displayed profile.
 */
export function GlobalCollectorSessionBar() {
  const { t } = useApp();
  const { activeSession, clearSession, hasActiveSession } = useActiveCollectorSession();
  const { pathname } = useLocation();
  const tc = t.collectorDashboard;

  // On the collection page the session block already shows the profile.
  if (!hasActiveSession || !activeSession || isCollectionPath(pathname)) return null;

  return (
    <div className="sticky top-[4.55rem] z-40 w-full border-b border-success/20 bg-background">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 sm:px-6">
        {/* Left: Session info */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Green pulse indicator */}
          <span className="flex h-6 w-6 shrink-0 items-center justify-center">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-success" />
          </span>

          {/* Profile info */}
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-semibold text-success">
              {tc.sessionActiveStatus || 'Session active'}
            </span>

            <span className="rounded-md border border-success/30 bg-success/10 px-2.5 py-0.5 font-mono text-xs font-bold tracking-wider text-success">
              {activeSession.code}
            </span>

            <span className="hidden items-center gap-1.5 text-xs text-success sm:flex">
              <User className="h-3 w-3" />
              <span>{activeSession.gender === 'masculin' ? 'M' : activeSession.gender === 'feminin' ? 'F' : 'A'}</span>
              <span>•</span>
              <span>{activeSession.location}</span>
            </span>
          </div>
        </div>

        {/* Right: Disconnect button */}
        <button
          type="button"
          onClick={clearSession}
          className="group flex shrink-0 items-center gap-2 rounded-full border border-success/20 bg-success/5 px-3 py-1.5 text-xs font-medium text-success transition-all hover:border-danger/40 hover:bg-danger/10 hover:text-danger"
          title={tc.endSession || 'End session'}
        >
          <X className="h-3.5 w-3.5 transition-colors group-hover:text-danger" />
          <span className="hidden sm:inline">
            {tc.endSession || 'Terminer'}
          </span>
        </button>
      </div>
    </div>
  );
}
